import dayjs from "dayjs"
import { useMemo } from "react"
import type { OperationContext } from "urql"
import { useQuery } from "urql"

import type { PurchasingCostsByWarehouse } from "../types"
import { isCurrentCost, isPastCost } from "../utils"

import { GetPurchasingCostListDocument, PurchasingCostListScope, type Query } from "@/graphql/codegen/graphql"

import "../graphql/queries"

interface UsePurchaseCostsForProductReturn {
  warehouses: PurchasingCostsByWarehouse[]
  isFetching: boolean
  refetch: (opts?: Partial<OperationContext>) => void
}

export const usePurchaseCostsForProduct = (productId: number): UsePurchaseCostsForProductReturn => {
  const [{ data, fetching }, refetch] = useQuery<Pick<Query, "priceChange">>({
    query: GetPurchasingCostListDocument,
    variables: {
      input: {
        productId,
        scope: PurchasingCostListScope.CurrentAndFuture,
      },
    },
    pause: !productId,
  })

  const warehouses = useMemo<PurchasingCostsByWarehouse[]>(() => {
    const output = data?.priceChange?.getPurchasingCostList
    if (output?.__typename !== "GetPurchasingCostListSuccess") return []

    const today = dayjs()
    const grouped = new Map<string, PurchasingCostsByWarehouse>()

    for (const cost of output.entries) {
      const location = cost.warehouseArea?.location
      if (!cost.warehouseAreaId || !location) continue

      let bucket = grouped.get(cost.warehouseAreaId)
      if (!bucket) {
        bucket = { warehouseAreaId: cost.warehouseAreaId, location, current: [], future: [] }
        grouped.set(cost.warehouseAreaId, bucket)
      }

      if (isCurrentCost(cost, today)) {
        bucket.current.push(cost)
      } else if (!isPastCost(cost, today)) {
        bucket.future.push(cost)
      }
    }

    return Array.from(grouped.values())
  }, [data])

  return { warehouses, isFetching: fetching, refetch }
}
